/**
 * Game state store. Owns:
 *  - the persistent settings (loaded from localStorage)
 *  - the live [World] state (driven by `VerticalEngine.step`)
 *  - the per-frame tick loop (requestAnimationFrame)
 *  - one-shot [GameEvent] fan-out for the UI to react to
 */
import { create } from 'zustand';
import { VerticalEngine } from './engine/VerticalEngine';
import type { PersistedSettings } from '../data/settings';
import {
  type Biome,
  type GameEvent,
  type World,
  RunStatus,
  SteerDir,
} from './engine/types';
import { loadSettings, saveSettings, recordRunStats, updateHighScore } from './persistence';
import * as audio from './audio';

export type Screen = 'start' | 'game' | 'gameOver';

export interface GameStore {
  screen: Screen;
  lastGameOver: { score: number; newHighScore: boolean; snakeLength: number } | null;
  lastLevelUp: { newLevel: number; biome: Biome; newScrollSpeed: number } | null;
  settings: PersistedSettings;
  world: World;
  cheatInvincible: boolean;
  newGame: () => void;
  togglePause: () => void;
  quitGame: () => void;
  setSteer: (dir: SteerDir) => void;
  consumeLevelUp: () => void;
  setSoundEnabled: (v: boolean) => void;
  toggleCheatInvincible: () => void;
}

const engine = new VerticalEngine();
const initialSettings = loadSettings();
const initialWorld = engine.newGame(
  initialSettings.highScore,
  initialSettings.longestRun,
  initialSettings.furthestLevel,
);

audio.setEnabled(initialSettings.soundEnabled);

export const useGameStore = create<GameStore>((set, get) => ({
  screen: 'start',
  lastGameOver: null,
  lastLevelUp: null,
  settings: initialSettings,
  world: initialWorld,
  cheatInvincible: false,

  newGame: () => {
    const { settings } = get();
    const world = engine.newGame(
      settings.highScore,
      settings.longestRun,
      settings.furthestLevel,
    );
    set({ world, screen: 'game', lastGameOver: null, lastLevelUp: null });
  },

  togglePause: () => {
    const w = get().world;
    if (w.status === RunStatus.RUNNING) {
      set({ world: engine.pause(w) });
    } else if (w.status === RunStatus.PAUSED) {
      set({ world: engine.resume(w) });
    }
  },

  quitGame: () => {
    set({ screen: 'start' });
  },

  setSteer: (dir) => {
    engine.setSteer(dir);
  },

  consumeLevelUp: () => {
    set({ lastLevelUp: null });
  },

  setSoundEnabled: (v) => {
    audio.setEnabled(v);
    const next = { ...get().settings, soundEnabled: v };
    saveSettings(next);
    set({ settings: next });
  },
  toggleCheatInvincible: () => {
    set({ cheatInvincible: !get().cheatInvincible });
  },
}));

let rafId: number | null = null;
let lastFrameNanos = 0;

// Dev-only: expose the store on window so a Playwright/curl smoke test
// can manipulate state for visual regression checks. Tree-shaken in
// production builds (Vite's `import.meta.env.DEV` is false).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __scrollsnake: typeof useGameStore }).__scrollsnake =
    useGameStore;
}

export function startTickLoop(): void {
  if (rafId !== null) return;
  lastFrameNanos = 0;
  const tick = (now: number) => {
    rafId = requestAnimationFrame(tick);
    // `now` from rAF is in milliseconds. Convert to seconds.
    const dt = lastFrameNanos === 0 ? 1 / 60 : Math.min((now - lastFrameNanos) / 1000, 1 / 30);
    lastFrameNanos = now;
    const store = useGameStore.getState();
    if (store.world.status !== RunStatus.RUNNING) return;
    const { world, events } = engine.step(store.world, dt, store.cheatInvincible);
    useGameStore.setState({ world });
    handleEvents(events, world);
  };
  rafId = requestAnimationFrame(tick);
}

export function stopTickLoop(): void {
  if (rafId !== null) cancelAnimationFrame(rafId);
  rafId = null;
  lastFrameNanos = 0;
}

function handleEvents(events: GameEvent[], world: World): void {
  const store = useGameStore.getState();
  const settings = store.settings;

  for (const ev of events) {
    switch (ev.type) {
      case 'ATE_FOOD':
        if (settings.soundEnabled) audio.playEat();
        break;
      case 'TURNED':
        if (settings.soundEnabled) audio.playTurn();
        break;
      case 'SCORE_MILESTONE':
        break;
      case 'LEVEL_UP':
        if (settings.soundEnabled) audio.playLevelUp();
        useGameStore.setState({ lastLevelUp: ev });
        break;
      case 'GAME_OVER': {
        if (settings.soundEnabled) audio.playGameOver();
        const updated = updateHighScore(settings, ev.score);
        const afterRun = recordRunStats(
          updated.settings,
          ev.score,
          ev.snakeLength,
          world.level,
        );
        useGameStore.setState({
          screen: 'gameOver',
          lastGameOver: { score: ev.score, newHighScore: ev.newHighScore, snakeLength: ev.snakeLength },
          settings: afterRun,
        });
        break;
      }
    }
  }
}
